import { type ReactNode, useEffect, useRef, useState } from 'react';

import type { AssetListItem, AssetType } from '@cartwhl/common/api/getAssets';
import type { BatchResponse, BatchStatus } from '@cartwhl/common/api/getBatch';
import PersonIcon from '@cartwhl/ui/icons/PersonIcon';
import WarningIcon from '@cartwhl/ui/icons/WarningIcon';

import AssetLibraryStackedThumbnail from '../AssetLibrary/components/AssetLibraryStackedThumbnail';
import AssetLibraryThumbnail from '../AssetLibrary/components/AssetLibraryThumbnail';
import BatchMotionThumbnailLoadingChip from './BatchMotionThumbnailLoadingChip';
import {
  BATCH_STACK_PEEK_WIDTH_PX,
  BatchHistoryHeroLetterboxMedia,
  BatchStackedThumbnailFrame,
  BatchStackedThumbnailGrayBackplate,
} from './BatchStackedThumbnailShell';

export type BatchHistoryThumbnailJob = Pick<
  BatchResponse,
  'batchID' | 'status' | 'thumbnailURL' | 'numTotalJobs'
> & {
  /** Client-resolved preview (e.g. mascot job outputVideoURL) when list API omits thumbnailURL. */
  resolvedPreviewURL?: string | null;
};

export function isBatchHistoryComplete(status: BatchStatus): boolean {
  return status === 'COMPLETED';
}

export function isBatchHistoryInFlight(status: BatchStatus): boolean {
  return (
    status === 'VALIDATING' || status === 'QUEUED' || status === 'IN_PROGRESS'
  );
}

/** Matches BatchCharacter `CHARACTER_THUMBNAIL_BACKGROUND` / ModelServing default. */
const CHARACTER_BATCH_HISTORY_LETTERBOX = '#8eb1ba';

function characterBatchHistoryPersonPlaceholder(compact?: boolean) {
  return (
    <PersonIcon
      className={
        compact
          ? 'text-gray-45 dark:text-gray-55 h-[min(83%,52px)] w-[min(83%,52px)]'
          : 'text-gray-45 dark:text-gray-55 h-[min(83%,119px)] w-[min(83%,119px)] sm:h-[min(83%,137px)] sm:w-[min(83%,137px)]'
      }
    />
  );
}

interface CharacterBatchHistoryJobThumbnailProps {
  batchID: string;
  status: BatchStatus;
  numTotalJobs: number;
  resolvedPreviewURL?: string;
  compact?: boolean;
}

/** Character Generator batch rows (recent jobs, dashboard history). */
export function CharacterBatchHistoryJobThumbnail({
  batchID,
  status,
  numTotalJobs,
  resolvedPreviewURL,
  compact,
}: CharacterBatchHistoryJobThumbnailProps) {
  const processing = isBatchHistoryInFlight(status);
  const failed = status === 'FAILED' || status === 'REJECTED';

  return (
    <BatchHistoryJobThumbnail
      job={{
        batchID,
        status,
        thumbnailURL: undefined,
        numTotalJobs,
        resolvedPreviewURL,
      }}
      thumbnailAssetType="MEDIA_IMAGE"
      heroLetterboxBackground={CHARACTER_BATCH_HISTORY_LETTERBOX}
      previewOnlyWhenComplete
      compact={compact}
      processing={processing}
      failed={failed}
      placeholder={characterBatchHistoryPersonPlaceholder(compact)}
    />
  );
}

export function getBatchHistoryThumbnailURL(
  job: BatchHistoryThumbnailJob,
  options?: { previewOnlyWhenComplete?: boolean },
): string | null {
  if (options?.previewOnlyWhenComplete && !isBatchHistoryComplete(job.status)) {
    return null;
  }
  const resolved = job.resolvedPreviewURL?.trim();
  if (resolved) {
    return resolved;
  }
  const url = job.thumbnailURL?.trim();
  if (!url) return null;
  if (isBatchHistoryComplete(job.status)) return url;
  return isBatchHistoryInFlight(job.status) ? url : null;
}

export function shouldStackBatchHistoryThumbnail(
  job: BatchHistoryThumbnailJob,
  options?: { processing?: boolean; hasPreviewMedia?: boolean },
): boolean {
  if (options?.processing) {
    return false;
  }
  if (job.numTotalJobs <= 1) {
    return false;
  }
  return options?.hasPreviewMedia ?? true;
}

function toBatchHistoryThumbnailAsset(
  batchID: string,
  thumbnailURL: string,
  assetType: AssetType,
): AssetListItem {
  return {
    assetID: batchID,
    assetName: batchID,
    assetType,
    createdAt: '',
    createdBy: '',
    createdByUser: '',
    lastUpdatedAt: '',
    thumbnailURL,
  };
}

interface BatchHistoryJobThumbnailProps {
  job: BatchHistoryThumbnailJob;
  thumbnailAssetType: AssetType;
  /** When set, completed batch thumbs use hero PNG letterbox (e.g. character `#8eb1ba`). */
  heroLetterboxBackground?: string;
  /** In-flight batches show placeholder only (character recent jobs). */
  previewOnlyWhenComplete?: boolean;
  compact?: boolean;
  processing?: boolean;
  failed?: boolean;
  placeholder: ReactNode;
}

export default function BatchHistoryJobThumbnail({
  job,
  thumbnailAssetType,
  heroLetterboxBackground,
  previewOnlyWhenComplete = false,
  compact,
  processing = false,
  failed = false,
  placeholder,
}: BatchHistoryJobThumbnailProps) {
  const [mediaFailed, setMediaFailed] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const mediaContainerRef = useRef<HTMLDivElement>(null);
  const previewThumb = getBatchHistoryThumbnailURL(job, {
    previewOnlyWhenComplete,
  });
  const showPreviewMedia = Boolean(previewThumb) && !failed && !mediaFailed;
  const stacked = shouldStackBatchHistoryThumbnail(job, {
    processing,
    hasPreviewMedia: showPreviewMedia,
  });

  useEffect(() => {
    setMediaFailed(false);
  }, [job.batchID, previewThumb]);

  useEffect(() => {
    const root = mediaContainerRef.current;
    if (!root || !showPreviewMedia) {
      setIsVisible(false);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        setIsVisible(entries.some((entry) => entry.isIntersecting));
      },
      { root: null, rootMargin: '64px', threshold: 0.01 },
    );
    observer.observe(root);
    return () => observer.disconnect();
  }, [showPreviewMedia, job.batchID]);

  if (showPreviewMedia && previewThumb) {
    const asset = toBatchHistoryThumbnailAsset(
      job.batchID,
      previewThumb,
      thumbnailAssetType,
    );
    const previewTile = heroLetterboxBackground ? (
      <BatchHistoryHeroLetterboxMedia
        src={previewThumb}
        letterboxColor={heroLetterboxBackground}
        stacked={stacked}
        onLoadError={() => setMediaFailed(true)}
      />
    ) : stacked ? (
      <AssetLibraryStackedThumbnail
        asset={asset}
        isHovered={isVisible}
        autoPlayVideo={isVisible}
        onLoadError={() => setMediaFailed(true)}
      />
    ) : (
      <AssetLibraryThumbnail
        assetType={thumbnailAssetType}
        thumbnailURL={previewThumb}
        isHovered={isVisible}
        autoPlayVideo={isVisible}
        onLoadError={() => setMediaFailed(true)}
      />
    );

    return (
      <div
        ref={mediaContainerRef}
        className="relative h-full w-full overflow-hidden"
        style={
          stacked ? { paddingRight: BATCH_STACK_PEEK_WIDTH_PX } : undefined
        }
      >
        {previewTile}
      </div>
    );
  }

  const placeholderTile = (
    <div className="relative flex h-full w-full items-center justify-center overflow-hidden">
      {placeholder}
      {failed && !processing ? (
        <div
          className={`pointer-events-none absolute bottom-2 right-2 z-10 shrink-0 ${compact ? 'h-8 w-8' : 'h-9 w-9'}`}
          aria-hidden
        >
          <div className="absolute inset-0 rounded-lg bg-white" />
          <div className="bg-red/10 dark:bg-red/10 absolute inset-0 flex items-center justify-center rounded-lg">
            <WarningIcon
              className={`text-red shrink-0 ${compact ? 'h-4 w-4' : 'h-5 w-5'}`}
            />
          </div>
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="bg-gray-30 dark:bg-gray-70 relative h-full w-full overflow-hidden">
      <BatchStackedThumbnailFrame
        enabled={stacked}
        backplate={<BatchStackedThumbnailGrayBackplate />}
      >
        {placeholderTile}
      </BatchStackedThumbnailFrame>
      {processing ? (
        <div
          className="pointer-events-none absolute inset-0 z-[60] bg-black/40 dark:bg-black/45"
          aria-hidden
        />
      ) : null}
      {processing ? (
        <div className="pointer-events-none absolute inset-0 z-[70]">
          <BatchMotionThumbnailLoadingChip compact={compact} />
        </div>
      ) : null}
    </div>
  );
}
